import { useState } from "react";
import {
    EMPTY_IPOH_AWNING_SPEC,
    IPOH_AWNING_CEILINGS,
    IPOH_AWNING_MATERIALS,
    IPOH_AWNING_PACKAGES,
    buildIpohAwningProduct,
    ipohPackageNeedsSize,
} from "../../../constants/ipohAwningCode";
import PosFieldLabel from "../../../components/shared/PosFieldLabel";
import { POS_STEP_CODES } from "../../../constants/workflowStepCodes";

const capitalizeSelection = (value) =>
    String(value || "")
        .toLowerCase()
        .replace(
            /(^|[^a-z\u00c0-\u024f])([a-z])/g,
            (_, lead, letter) => lead + letter.toUpperCase()
        );

const IpohAwningCodeSelector = ({ itemDetails, setItemDetails, children, colors, onMaterialsChange }) => {
    const [openSections, setOpenSections] = useState({
        materials: true,
        package: false,
        ceiling: false,
        specification: false,
        color: false,
    });
    const spec = {
        ...EMPTY_IPOH_AWNING_SPEC,
        ...(itemDetails.ipohAwningSpec || {}),
    };
    const preview = buildIpohAwningProduct(spec);

    const toggleSection = (section) => {
        setOpenSections((current) => ({
            ...current,
            [section]: !current[section],
        }));
    };

    const updateSpec = (next) => {
        const built = buildIpohAwningProduct(next);
        setItemDetails((prev) => {
            const previousBuilt = buildIpohAwningProduct({
                ...EMPTY_IPOH_AWNING_SPEC,
                ...(prev.ipohAwningSpec || {}),
            });
            const currentName = prev.ipohProductName;
            const keepCustomName =
                currentName != null &&
                String(currentName).trim() !== "" &&
                String(currentName).trim() !== String(previousBuilt.name).trim();
            return {
                ...prev,
                ipohAwningSpec: next,
                ipohProductCode: built.code,
                ipohProductName: keepCustomName ? currentName : built.name,
            };
        });
    };

    const toggleMaterial = (code) => {
        const materials = spec.materials.includes(code)
            ? spec.materials.filter((item) => item !== code)
            : [...spec.materials, code];
        updateSpec({ ...spec, materials });
        onMaterialsChange?.(materials);
    };

    const renderOption = ({ key, selected, type, name, onChange, code, label }) => (
        <label
            key={key}
            className={`ipoh-awning-option${type === "radio" ? " radio" : ""}${selected ? " selected" : ""}`}
        >
            <input type={type} name={name} checked={selected} onChange={onChange} />
            <span className="ipoh-awning-mark" aria-hidden="true" />
            <span className="ipoh-awning-code">{code}</span>
            <span className="ipoh-awning-name">{capitalizeSelection(label)}</span>
        </label>
    );

    const renderSection = (id, title, hint, summary, children) => {
        const open = openSections[id];
        return (
            <section className={`ipoh-awning-card${open ? " open" : ""}`}>
                <button
                    type="button"
                    className="ipoh-awning-card-header"
                    onClick={() => toggleSection(id)}
                    aria-expanded={open}
                >
                    <span className="ipoh-awning-card-title">{title}</span>
                    <span className="ipoh-awning-card-hint">{open ? hint : summary || hint}</span>
                    <span className="ipoh-awning-chevron" aria-hidden="true" />
                </button>
                {open && <div className="ipoh-awning-options">{children}</div>}
            </section>
        );
    };

    const materialSummary = [
        ...spec.materials,
        spec.pipe ? "(P)" : "",
    ]
        .filter(Boolean)
        .join(" + ");
    const packageItem = IPOH_AWNING_PACKAGES.find((item) => item.code === spec.package);
    const packageSummary = [
        capitalizeSelection(packageItem?.label),
        ipohPackageNeedsSize(spec.package) && spec.packageSize ? `${spec.packageSize}''` : "",
    ]
        .filter(Boolean)
        .join(" ");
    const ceilingSummary = capitalizeSelection(
        IPOH_AWNING_CEILINGS.find((item) => item.code === spec.ceiling)?.name
    );

    return (
        <div className="ipoh-awning-picker">
            {renderSection(
                "materials",
                "1. Materials",
                "Select more than one",
                materialSummary,
                <>
                    {IPOH_AWNING_MATERIALS.map((item) =>
                        renderOption({
                            key: item.code,
                            type: "checkbox",
                            selected: spec.materials.includes(item.code),
                            onChange: () => toggleMaterial(item.code),
                            code: item.code,
                            label: item.label || item.name,
                        })
                    )}
                    {renderOption({
                        key: "pipe",
                        type: "checkbox",
                        selected: spec.pipe,
                        onChange: () => updateSpec({ ...spec, pipe: !spec.pipe }),
                        code: "(P)",
                        label: "+ \u540aPIPE DESIGN",
                    })}
                </>
            )}

            {renderSection(
                "package",
                "2. Package",
                "Choose one",
                packageSummary,
                <>
                    {IPOH_AWNING_PACKAGES.map((item) =>
                        renderOption({
                            key: item.code,
                            type: "radio",
                            name: "ipoh-awning-package",
                            selected: spec.package === item.code,
                            onChange: () =>
                                updateSpec({
                                    ...spec,
                                    package: item.code,
                                    packageSize: ipohPackageNeedsSize(item.code)
                                        ? spec.packageSize
                                        : "",
                                }),
                            code: item.code,
                            label: item.label || item.name,
                        })
                    )}
                    {ipohPackageNeedsSize(spec.package) && (
                        <div className="ipoh-awning-profile-size">
                            <span className="ipoh-awning-profile-size-label">
                                Aluminium G Profile size
                            </span>
                            <div className="ipoh-awning-profile-size-options">
                                {["8", "12"].map((size) => (
                                    <button
                                        key={`g-profile-${size}`}
                                        type="button"
                                        className={spec.packageSize === size ? "selected" : ""}
                                        onClick={() => updateSpec({ ...spec, packageSize: size })}
                                    >
                                        {`${size}''`}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {renderSection(
                "ceiling",
                "3. Ceiling",
                "Optional, choose one",
                ceilingSummary,
                IPOH_AWNING_CEILINGS.map((item) =>
                    renderOption({
                        key: item.code,
                        type: "radio",
                        name: "ipoh-awning-ceiling",
                        selected: spec.ceiling === item.code,
                        onChange: () =>
                            updateSpec({
                                ...spec,
                                ceiling: spec.ceiling === item.code ? "" : item.code,
                            }),
                        code: item.code,
                        label: item.label || item.name,
                    })
                )
            )}

            {children && (
                <section className={`ipoh-awning-card${openSections.specification ? " open" : ""}`}>
                    <button
                        type="button"
                        className="ipoh-awning-card-header"
                        onClick={() => toggleSection("specification")}
                        aria-expanded={openSections.specification}
                    >
                        <span className="ipoh-awning-card-title">4. Specification</span>
                        <span className="ipoh-awning-card-spacer" />
                        <span className="ipoh-awning-chevron" aria-hidden="true" />
                    </button>
                    {openSections.specification && (
                        <div className="ipoh-awning-specification">{children}</div>
                    )}
                </section>
            )}

            {colors && (
                <section className={`ipoh-awning-card${openSections.color ? " open" : ""}`}>
                    <button
                        type="button"
                        className="ipoh-awning-card-header"
                        onClick={() => toggleSection("color")}
                        aria-expanded={openSections.color}
                    >
                        <span className="ipoh-awning-card-title">5. Color</span>
                        <span className="ipoh-awning-card-spacer" />
                        <span className="ipoh-awning-chevron" aria-hidden="true" />
                    </button>
                    {openSections.color && (
                        <div className="ipoh-awning-specification">{colors}</div>
                    )}
                </section>
            )}

            <div className="ipoh-awning-result">
                <div className="addProductItem fullWidth">
                    <label>Product Code</label>
                    <input value={preview.code} readOnly />
                </div>
                <div className="addProductItem fullWidth">
                    <label>Product Name</label>
                    <input
                        value={
                            itemDetails.ipohProductName !== undefined
                                ? itemDetails.ipohProductName
                                : preview.name
                        }
                        onChange={(e) =>
                            setItemDetails((prev) => ({
                                ...prev,
                                ipohProductName: e.target.value,
                            }))
                        }
                    />
                </div>
                <div className="addProductItem fullWidth">
                    <PosFieldLabel code={POS_STEP_CODES.price}>Price</PosFieldLabel>
                    <input
                        name="price"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Enter price"
                        value={
                            itemDetails.price === undefined ||
                            itemDetails.price === null ||
                            itemDetails.price === ""
                                ? ""
                                : itemDetails.price
                        }
                        onChange={(e) =>
                            setItemDetails((prev) => ({
                                ...prev,
                                price: e.target.value,
                            }))
                        }
                    />
                </div>
            </div>
        </div>
    );
};

export default IpohAwningCodeSelector;
